import { useState } from 'react';
import axios from 'axios';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { ArrowButton, MaskedLines, Reveal } from './bits';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[0-9+()\-\s.]{7,20}$/;

const inputCls =
  'w-full border border-ink/15 bg-white px-4 py-3.5 text-sm text-ink placeholder:text-neutral-400 transition-colors focus:border-forest focus:outline-none';

function Field({ label, error, required = true, children, htmlFor }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-2 block font-display text-xs font-bold uppercase tracking-[0.14em] text-ink">
        {label} {required && <span className="text-[#C6A15B]" aria-hidden="true">*</span>}
      </label>
      {children}
      {error && (
        <p role="alert" className="mt-1.5 text-xs font-medium text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}

export default function QuoteForm({ initialService = '' }) {
  const { t, lang } = useLanguage();
  const f = t.form;
  const [values, setValues] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address: '',
    service: initialService,
    frequency: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState('');

  const set = (key) => (e) => {
    const v = e && e.target ? e.target.value : e;
    setValues((prev) => ({ ...prev, [key]: v }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!values.firstName.trim()) errs.firstName = f.errors.required;
    if (!values.lastName.trim()) errs.lastName = f.errors.required;
    if (!PHONE_RE.test(values.phone.trim())) errs.phone = f.errors.phone;
    if (!EMAIL_RE.test(values.email.trim())) errs.email = f.errors.email;
    if (!values.address.trim()) errs.address = f.errors.required;
    if (!values.service) errs.service = f.errors.required;
    if (!values.frequency) errs.frequency = f.errors.required;
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSending(true);
    setSendError('');
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/quotes`, {
        first_name: values.firstName,
        last_name: values.lastName,
        phone: values.phone,
        email: values.email,
        address: values.address,
        service: values.service,
        frequency: values.frequency,
        message: values.message,
        locale: lang,
      });
      setSent(true);
    } catch (err) {
      setSendError(f.errorGeneric);
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="flex min-h-[420px] flex-col items-start justify-center" data-testid="quote-success">
        <MaskedLines
          as="h2"
          lines={[f.successTitle]}
          className="font-display text-4xl font-extrabold uppercase tracking-tight text-forest sm:text-5xl"
        />
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-md text-base leading-relaxed text-neutral-600 sm:text-lg">{f.successBody}</p>
        </Reveal>
        <Reveal delay={0.3}>
          <ArrowButton
            variant="outlineDark"
            className="mt-10"
            dataTestId="quote-again-button"
            onClick={() => {
              setSent(false);
              setValues({ firstName: '', lastName: '', phone: '', email: '', address: '', service: '', frequency: '', message: '' });
            }}
          >
            {f.again}
          </ArrowButton>
        </Reveal>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate data-testid="quote-form" className="space-y-6">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <Field label={f.firstName} htmlFor="firstName" error={errors.firstName}>
          <input id="firstName" data-testid="quote-first-name" className={inputCls} value={values.firstName} onChange={set('firstName')} autoComplete="given-name" aria-required="true" aria-invalid={!!errors.firstName} />
        </Field>
        <Field label={f.lastName} htmlFor="lastName" error={errors.lastName}>
          <input id="lastName" data-testid="quote-last-name" className={inputCls} value={values.lastName} onChange={set('lastName')} autoComplete="family-name" aria-required="true" aria-invalid={!!errors.lastName} />
        </Field>
      </div>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <Field label={f.phone} htmlFor="phone" error={errors.phone}>
          <input id="phone" type="tel" data-testid="quote-phone" className={inputCls} value={values.phone} onChange={set('phone')} autoComplete="tel" aria-required="true" aria-invalid={!!errors.phone} />
        </Field>
        <Field label={f.email} htmlFor="email" error={errors.email}>
          <input id="email" type="email" data-testid="quote-email" className={inputCls} value={values.email} onChange={set('email')} autoComplete="email" aria-required="true" aria-invalid={!!errors.email} />
        </Field>
      </div>
      <Field label={f.address} htmlFor="address" error={errors.address}>
        <input id="address" data-testid="quote-address" className={inputCls} value={values.address} onChange={set('address')} autoComplete="street-address" aria-required="true" aria-invalid={!!errors.address} />
      </Field>
      <Field label={f.service} htmlFor="service" error={errors.service}>
        <div className="relative">
          <select
            id="service"
            data-testid="quote-service"
            className={`${inputCls} appearance-none pr-10 ${values.service ? '' : 'text-neutral-400'}`}
            value={values.service}
            onChange={set('service')}
            aria-required="true"
            aria-invalid={!!errors.service}
          >
            <option value="" disabled>
              {f.service}
            </option>
            {f.serviceOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" aria-hidden="true" />
        </div>
      </Field>
      <Field label={f.frequency} error={errors.frequency} htmlFor="frequency-one-time">
        <div role="radiogroup" aria-label={f.frequency} className="flex flex-wrap gap-3">
          {f.frequencyOptions.map((o, i) => (
            <button
              key={o.value}
              id={i === 0 ? 'frequency-one-time' : undefined}
              type="button"
              role="radio"
              aria-checked={values.frequency === o.value}
              data-testid={`quote-frequency-${o.value}`}
              onClick={() => set('frequency')(o.value)}
              className={`border px-5 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] transition-colors ${
                values.frequency === o.value
                  ? 'border-forest bg-forest text-white'
                  : 'border-ink/20 text-ink hover:border-forest'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </Field>
      <Field label={f.message} htmlFor="message" required={false}>
        <textarea
          id="message"
          rows={4}
          data-testid="quote-message"
          className={`${inputCls} resize-y`}
          placeholder={f.messagePlaceholder}
          value={values.message}
          onChange={set('message')}
        />
      </Field>
      {sendError && (
        <p role="alert" data-testid="quote-error" className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {sendError}
        </p>
      )}
      <ArrowButton type="submit" variant="green" disabled={sending} dataTestId="quote-submit" className="w-full sm:w-auto">
        {sending ? f.submitting : f.submit}
      </ArrowButton>
    </form>
  );
}
